import { Box, Group, Stack, Text, Anchor } from '@mantine/core'
import { useI18n } from './_i18n'
import { storeUrl, storeBadge, trackStoreClick } from './_appStore'

// Heights inherited from the AdSense units these replaced (_AdUnit's
// RESERVED_HEIGHTS), so removing the ads changed what sits in the slots without
// changing where anything below them lands.
const MIN_HEIGHT = {
  horizontal: 90,
  rectangle: 250,
}

function StoreBadge({ store, height, placement }) {
  const { t, locale } = useI18n()
  const ios = store === 'ios'
  const label = t(ios ? 'app_store' : 'google_play')
  // Localized artwork, with the width that belongs to THIS badge — see
  // storeBadge(). Width and height are always both set; the footer comment in
  // _Layout.jsx has the layout-shift measurement that made that a rule.
  const badge = storeBadge(store, locale, height)
  return (
    // data-store is what the pre-paint CSS in the layout head keys off to
    // hide the store this visitor cannot install from (desktop hides the
    // whole [data-app-promo] unit instead — promos are mobile-only). Both
    // badges are always in the markup — the HTML is identical for every
    // visitor, so a CDN caching a station page cannot serve an iPhone the
    // Android-only variant.
    <Anchor
      href={storeUrl(store, locale)}
      target="_blank"
      rel="noopener"
      aria-label={label}
      data-store={store}
      onClick={() => trackStoreClick(store, placement)}
    >
      <img
        src={badge.src}
        alt={label}
        width={badge.width}
        height={badge.height}
        style={{ display: 'block' }}
      />
    </Anchor>
  )
}

// Full-card tap target, like the ad units these replaced: an invisible link
// stretched over the whole promo. One per store, tagged data-store so the
// platform CSS in the layout head shows only the installable one — same
// mechanism as the badges, and the HTML stays identical for every visitor.
// aria-hidden + tabIndex -1 keep it out of the accessibility tree and tab
// order; the visible badge anchor remains the semantic link.
function StoreOverlay({ store, placement }) {
  const { locale } = useI18n()
  return (
    <a
      href={storeUrl(store, locale)}
      target="_blank"
      rel="noreferrer"
      aria-hidden="true"
      tabIndex={-1}
      data-store={store}
      onClick={() => trackStoreClick(store, placement)}
      style={{ position: 'absolute', inset: 0, zIndex: 1 }}
    />
  )
}

// In-page prompt to install the native app, in the slots the AdSense units used
// to occupy. `variant` picks the footprint; `placement` is only a label for the
// Plausible event, so clicks can be attributed to a slot rather than a page.
// `title` and `body` replace the generic copy where the slot has something more
// specific to say (the route planner's "Follow this route in the app").
export default function AppPromo({
  variant = 'horizontal',
  placement,
  className = '',
  title: titleText,
  body,
}) {
  const { t } = useI18n()
  const rect = variant === 'rectangle'
  const badgeHeight = rect ? 44 : 36

  const title = titleText || t('app_promo_title')

  const badges = (
    <Group gap="xs" justify={rect ? 'center' : 'flex-start'} wrap="wrap">
      <StoreBadge store="ios" height={badgeHeight} placement={placement} />
      <StoreBadge store="android" height={badgeHeight} placement={placement} />
    </Group>
  )

  const icon = (
    <img
      src="/apple-touch-icon.png"
      alt=""
      width={rect ? 64 : 48}
      height={rect ? 64 : 48}
      style={{
        display: 'block',
        borderRadius: rect ? 14 : 10,
        flexShrink: 0,
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
      }}
    />
  )

  return (
    // Styled as a brand tile rather than an outlined white card: the promo sits
    // among the navy data tiles (TILE in stations/_tiles.jsx), and a white box
    // there read as empty chrome. The gradient runs bright→body-navy so it
    // belongs to the family without impersonating a temporal tile (those are
    // flat). Same surface in light and dark, like the tiles; the hairline
    // border is what separates it from the dark-mode body background.
    <Box
      data-app-promo=""
      className={className}
      p={rect ? 'lg' : 'md'}
      style={{
        minHeight: MIN_HEIGHT[variant] ?? MIN_HEIGHT.horizontal,
        background: 'linear-gradient(135deg, #0d5379 0%, #062534 100%)',
        color: '#fff',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Anchors the stretched StoreOverlay links to this card.
        position: 'relative',
      }}
    >
      <StoreOverlay store="ios" placement={placement} />
      <StoreOverlay store="android" placement={placement} />
      {rect ? (
        <Stack gap="sm" align="center" ta="center">
          {icon}
          <Stack gap={2}>
            <Text fw={700} c="#fff">
              {title}
            </Text>
            <Text size="sm" c="rgba(255,255,255,0.75)">
              {body || t('app_promo_body')}
            </Text>
          </Stack>
          {badges}
        </Stack>
      ) : (
        // Three fixed columns — icon | title | badge — with the title as the
        // flexible middle: it wraps to two lines rather than pushing the badge
        // onto its own row.
        <Group gap="sm" wrap="nowrap" w="100%">
          {icon}
          <Text fw={700} size="md" lh={1.3} c="#fff" style={{ flex: 1, minWidth: 0 }}>
            {title}
          </Text>
          <Box style={{ flexShrink: 0 }}>{badges}</Box>
        </Group>
      )}
    </Box>
  )
}
